/*
 * @providesModule AddFriendModal
 * 
 */

import React from 'react';

import _ from 'lodash';
import {_m} from 'INTL';
import AWSManager from 'AWSManager';
import ChatManager from 'ChatManager';
import ChatStore from 'ChatStore';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import ErrorDialogModal from 'ErrorDialogModal';
import FriendsTournamentApiController from 'FriendsTournamentApiController';
import I18n from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController from 'ModalNavigationController';
import PropTypes from 'prop-types';
import SkillzStyle from 'SkillzStyle';
import SKZTournament from 'SKZTournament';
import StaticWarningModal from 'StaticWarningModal';

import {
  StyleSheet,
} from 'react-native';

const CANCEL_BUTTON = 0;
const ADD_FRIEND_BUTTON = 1;

const NEW_USER_MAX_GAMES = 10;

const {
  btnTextColorOne,
  fontSecondary,
  fontSizeH4,
} = SkillzStyle.fullStyles;

export default class AddFriendModal extends React.Component {
  // Completion returns false when adding friend was required and did not end up as friends or with request sent. The second argument is true if the add friends modal was displayed.
  static showIfNeeded(tournament: SKZTournament, shouldRewardCreator: boolean, fromResultsCheck: boolean, completion: (arg: boolean, arg2?: boolean)=>void) {
    if (tournament) {
      if (!tournament.friend) {
        console.debug('AddFriendModal: not a friends tournament, do not display');
        completion(true);
        return;
      }
    }

    CurrentUserStore.getCurrentUserId().then((userId: string) => {
      if (!fromResultsCheck && !tournament.isIncomingInviteFromOtherUser(userId)) {
        const modal = (
          <StaticWarningModal message={I18n.message('You have reached the maximum number of entries for this tournament.')}
                              buttonTitleArray={[I18n.message('CANCEL')]}
                              buttonTextStyling={styles.cancelButton}
                              callback={() => { completion(false); ModalNavigationController.hideModal(); }} />
        );

        ModalNavigationController.showModal(modal, {animationType: 'fade', transparent: true});
        return;
      }

      if (!fromResultsCheck && tournament.isOffSystemFTTemplate()) {
        console.debug('AddFriendModal: is an off_system friends tournament, do not display');
        completion(true);
        return;
      }

      LoadingIndicator.show();
      console.debug('AddFriendModal: attempting to connect to chat for friends check');
      connectToChat((success: boolean) => {
        LoadingIndicator.hide();
        if (!success) {
          console.debug('AddFriendModal: failed to connect to chat for friends check');
          completion(false);
          return;
        }
        console.debug('AddFriendModal: connected to chat for friends check');

        if (this._hasAddedUser(tournament)) {
          console.debug('AddFriendModal: user is already added as friend, continue');
          completion(true);
          return;
        }
        console.debug('AddFriendModal: user is not already added as friend, show modal');

        if (fromResultsCheck && tournament.isOffSystemFTTemplate()) {
          AWSManager.trackEvent('VS_FRIENDS_OFFSYSTEM_ADD_FRIEND_MODAL');
        }

        const modal = (
          <AddFriendModal tournament={tournament}
                          shouldRewardCreator={shouldRewardCreator}
                          onAdd={() => { completion(true, true); }}
                          onCancel={() => { completion(false, true); }} />
        );
        AWSManager.trackEvent('VS_FRIENDS_PLAY_ADD_FRIEND_MODAL');
        ModalNavigationController.showModal(modal, {animationType: 'fade', transparent: true});
      });
    });
  }

  static _hasAddedUser(tournament: SKZTournament): boolean {
    return ChatManager.isUserAContact(tournament.creator.user_id) || ChatManager.isUserRequestPending(tournament.creator.user_id);
  }

  static propTypes = {
    tournament: PropTypes.instanceOf(SKZTournament).isRequired,
    onAdd: PropTypes.func, // Called after user accepts and friend request is sent
    onCancel: PropTypes.func,
    shouldRewardCreator: PropTypes.bool,
  };

  constructor(props: unknown) {
    super(props);
    this.profilePromise = CurrentUserStore.fetchUserProfile(true);
    this.success = false;
  }

  _onDismiss() {
    ModalNavigationController.hideModal();
    _.invoke(this.props, 'onCancel');
  }

  _onAdd() {
    this.success = true;
    this._addChallengeOnHostIfNeeded();
    _.invoke(this.props, 'onAdd');
  }

  _onButtonPress(index: number) {
    if (index == CANCEL_BUTTON) {
      AWSManager.trackEvent('VS_FRIENDS_PLAY_ADD_FRIEND_MODAL_CANCEL');
      _.invoke(this.props, 'onCancel');
    } else if (index == ADD_FRIEND_BUTTON) {
      AWSManager.trackEvent('VS_FRIENDS_PLAY_ADD_FRIEND_MODAL_ADD');
      this._addFriend();
    }

    ModalNavigationController.hideModal();
  }

  _addFriend() {
    if (!this.props.tournament || !this.props.tournament.creator) {
      _.invoke(this.props, 'onCancel');
    }
    ChatManager.connectWithCompletion((connected: boolean) => {
      if (connected) {
        if (ChatManager.isUserIncomingRequestPending(this.props.tournament.creator.user_id)) {
          this._acceptPendingRequest();
        } else {
          this._sendRequest();
        }
      } else {
        this._showErrorModal(this._addFriend.bind(this));
      }
    });
  }

  _addChallengeOnHostIfNeeded() {
    if (this.props.shouldRewardCreator) {
      this.profilePromise.then((profile: Object) => {
        if (profile.total_games_played < NEW_USER_MAX_GAMES) {
          FriendsTournamentApiController.addIncentiveChallengeToFriend(this.props.tournament.creator.user_id);
        } else {
        }
      }).catch((error: unknown) => {
      });
    }
  }

  _sendRequest() {
    ChatManager.subscribeToContact(this.props.tournament.creator.user_id).then(() => {
      this._onAdd();
    }).catch((error: unknown) => {
      this._showErrorModal(this._sendRequest.bind(this));
    });
  }

  _acceptPendingRequest() {
    const success = ChatManager.approveContactSubscription(this.props.tournament.creator.user_id);
    if (!success) {
      this._showErrorModal(this._acceptPendingRequest.bind(this));
      return;
    }

    ChatManager.createRoomForUsers([this.props.tournament.creator.user_id], (error: String, result: Object) => {
      if (result) {
        this._sendThanksMessage(result);
      }
      // Still basically success either way since rooms get created if necessary when entering anyway
      this._onAdd();
    });
  }

  _showErrorModal(retry: ()=>void) {
    const onDone = () => {
      ModalNavigationController.hideModal();
      if (!this.success) {
        _.invoke(this.props, 'onCancel');
      }
    };

    const errorModal = (
      <ErrorDialogModal retryCallback={retry}
                        onDone={onDone} 
                        error={{message: 'Chat is enabled for player but could not connect to chat server'}}/>
    );
    const modalProps = {
      animationType: 'fade',
      transparent: true,
    };
    ModalNavigationController.showModal(errorModal, modalProps);
  }

  _sendThanksMessage(roomName: string) {
    ChatManager.sendMessageToRoom(_m('Thanks for adding me!'), roomName);
  }

  _getTitleText(): string {
    if (!this.props.tournament || !this.props.tournament.creator) {
      return null;
    }
    if (_.invoke(this.props.tournament, 'isOffSystemFTTemplate')) {
      return I18n.message('Send a friend request?');
    } else {
      return null;
    }
  }

  _getMessageText(): string {
    if (!this.props.tournament || !this.props.tournament.creator) {
      return '';
    }
    
    if (_.invoke(this.props.tournament, 'isOffSystemFTTemplate')) {
      return I18n.formattedMessage('Add <b>{0}</b> to chat and play VS each other!', this.props.tournament.creator.display_name);
    }
    return I18n.formattedMessage('You must add <b>{0}</b> as a Friend to play with them', this.props.tournament.creator.display_name);
  }

  render(): React.ReactNode {
    return (
      <StaticWarningModal title={this._getTitleText()}
                          message={this._getMessageText()}
                          buttonTitleArray={[I18n.message('CANCEL'), I18n.message('ADD FRIEND')]}
                          callback={this._onButtonPress.bind(this)}
                          onDismiss={this._onDismiss.bind(this)} />
    );
  }
}

const connectToChat = (completion: (boolean)=>void) => {
  if (ClientPropertyConstants.getIsPlayerChatEnabled()) {
    console.debug('AddFriendModal: connectToChat start initialization of chat settings');
    ChatStore.initializeChatSettings(false, (payload?: Object, error?: Object) => {
      if (payload) {
        console.debug('AddFriendModal: connectToChat initialization of chat settings passed, start connecting');
        ChatManager.setConnectionInfo(payload);
        ChatManager.connectWithCompletion((success: boolean) => {
          console.debug('AddFriendModal: connectToChat connecting succeeded');
          completion(success);
        });
      } else {
        console.debug('AddFriendModal: connectToChat initialization of chat settings failed');
        completion(false);
      }
    });
  } else {
    console.debug('AddFriendModal: connectToChat player is not enabled for chat');
    completion(false);
  }
};

const styles = StyleSheet.create({
  cancelButton: {
    ...fontSizeH4,
    ...btnTextColorOne,
    ...fontSecondary,
  },
});
